import { useCallback, useContext, useEffect, useState } from 'react';
import type { CommandInfo } from '@casa/driver-sdk';
import type { Bridge } from '@casa/bridge';
import type { TileEnvironment } from '@casa/tiles';
import { resolveSource } from './data_sources.ts';
import { LiveDataContext, useReadings, useReadingsList, useTileSources, useTileStatus } from './live_data.ts';

/**
 * The environment the tile views of `@casa/tiles` get from this app: readings and status from
 * live data (`LiveDataContext`), sources resolved by the names in the tiles' options, commands
 * sent through the runtime to the sources' drivers, and what those take, asked of the drivers.
 */
export function createTileEnvironment(bridge: Bridge | null, locale: string | undefined) : TileEnvironment {
    return {
        useReadings: (tile, name) => useReadings(resolveSource(useTileSources(tile), name)),
        useReadingsList(tile, names) {
            const sources = useTileSources(tile);

            return useReadingsList(names.map((name) => resolveSource(sources, name)));
        },
        useCommand(tile, name) {
            const live = useContext(LiveDataContext);
            const ref = resolveSource(useTileSources(tile), name);

            return useCallback(
                async (command, arg, optimistic) => {
                    if (!live || !ref) {
                        throw new Error('this tile has no data source to send the command to');
                    }

                    await live.command(ref, command, arg, optimistic);
                },
                [live, ref],
            );
        },
        useCommands(tile, name) {
            const live = useContext(LiveDataContext);
            const ref = resolveSource(useTileSources(tile), name);
            const [described, setDescribed] = useState<{ ref: typeof ref; commands: readonly CommandInfo[] }>();
            useEffect(() => {
                if (!live || !ref) {
                    return undefined;
                }

                let isCurrent = true;
                live.describe(ref).then(
                    (description) => {
                        if (isCurrent) {
                            setDescribed({ ref, commands: description.commands });
                        }
                    },
                    (error: unknown) => {
                        // The tile does without: a set point's usual range, no modes.
                        console.warn(`describing ${ref.source.id}: ${error instanceof Error ? error.message : String(error)}`);
                    },
                );

                return () => {
                    isCurrent = false;
                };
            }, [live, ref]);

            return described !== undefined && described.ref === ref ? described.commands : undefined;
        },
        useStatus: useTileStatus,
        locale,
        bridge,
    };
}
